import React from 'react';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress';

/**
 * Props para o componente StepperBase.
 *
 * @property {number} steps - Número total de passos no stepper.
 * @property {number} currentStep - Índice do passo atual (baseado em 0).
 */
interface StepperProps {
  steps: number;
  currentStep: number;
}

/**
 * Estilo personalizado para as barras de progresso do MUI.
 */
const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  width: '100%',
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.primary.light,
    ...theme.applyStyles?.('dark', {
      backgroundColor: theme.palette.primary.light,
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.primary.main,
    ...theme.applyStyles?.('dark', {
      backgroundColor: theme.palette.primary.light,
    }),
  },
}));

/**
 * Componente StepperBase.
 *
 * Componente de barra de progresso que representa múltiplos passos em sequência.
 * Ele indica o progresso em um fluxo linear, destacando visualmente o estado de cada passo.
 *
 * @param {StepperProps} props - Props do componente.
 * @returns {JSX.Element} O stepper com barras de progresso organizadas lado a lado.
 *
 * @example
 * // Uso básico do componente com 5 passos
 * <StepperBase steps={5} currentStep={2} />
 */
export const StepperBase: React.FC<StepperProps> = ({ steps, currentStep }) => {
  return (
    <Stack direction="row" spacing={2} sx={{ flexGrow: 1 }}>
      {Array.from({ length: steps }).map((_, index) => {
        let value = 0;

        if (index < currentStep) {
          value = 100;
        } else if (index === currentStep) {
          value = -10;
        }

        return (
          <BorderLinearProgress
            key={index}
            variant="determinate"
            value={value}
          />
        );
      })}
    </Stack>
  );
};
